import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconWrapper } from '../components/common/IconWrapper';
import {
  HiArrowLeft,
  HiMapPin,
  HiClock,
  HiUsers,
  HiCodeBracket,
  HiChartBar,
  HiCloud,
  HiServer,
  HiCheckCircle,
  HiXMark,
  HiDocumentArrowUp,
  HiEnvelope,
  HiPhone,
  HiUser
} from 'react-icons/hi2';
import { AnimatePresence } from 'framer-motion';
import { submitJobApplication } from '../lib/api';

// Job positions data (same as CareersPage)
const jobPositions = [
  {
    id: 'full-stack-developer-intern',
    title: 'Full Stack Developer (MERN Stack)',
    department: 'Software Development',
    location: 'Indore',
    type: 'Internship',
    description: 'Join our team as an intern and gain hands-on experience in building modern web applications using the MERN stack (MongoDB, Express.js, React, Node.js). Work on real-world projects, collaborate with experienced developers, and learn industry best practices in full-stack development.',
    requirements: [
      'Basic knowledge of JavaScript, HTML, CSS',
      'Understanding of React.js fundamentals',
      'Familiarity with Node.js and Express.js',
      'Knowledge of MongoDB or any database',
      'Strong problem-solving skills',
      'Good communication skills',
      'Willingness to learn and adapt'
    ],
    responsibilities: [
      'Assist in developing and maintaining web applications using MERN stack',
      'Write clean, maintainable, and efficient code',
      'Participate in code reviews and team meetings',
      'Learn and apply best practices in software development',
      'Collaborate with senior developers on project tasks',
      'Debug and fix issues in existing applications'
    ],
    icon: HiCodeBracket,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'full-stack-developer',
    title: 'Full Stack Developer (MERN Stack)',
    department: 'Software Development',
    location: 'Indore',
    type: 'Full-time',
    description: 'We are seeking an experienced Full Stack Developer with 2-5 years of experience in MERN stack development. You will be responsible for designing, developing, and maintaining scalable web applications. Work on cutting-edge projects, collaborate with cross-functional teams, and contribute to the entire software development lifecycle.',
    requirements: [
      '2-5 years of experience in MERN stack development',
      'Strong proficiency in JavaScript, React.js, Node.js, and Express.js',
      'Experience with MongoDB and database design',
      'Knowledge of RESTful APIs and GraphQL',
      'Familiarity with Git version control',
      'Experience with cloud platforms (AWS/Azure)',
      'Understanding of Agile/Scrum methodologies',
      'Strong debugging and problem-solving skills'
    ],
    responsibilities: [
      'Design and develop scalable web applications using MERN stack',
      'Write clean, efficient, and well-documented code',
      'Collaborate with cross-functional teams to define and implement new features',
      'Optimize applications for maximum speed and scalability',
      'Implement security and data protection measures',
      'Participate in code reviews and maintain code quality standards',
      'Troubleshoot and debug applications',
      'Stay updated with emerging technologies and industry trends'
    ],
    icon: HiCodeBracket,
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'azure-data-engineer',
    title: 'Azure Data Engineer',
    department: 'Data Engineering',
    location: 'Indore',
    type: 'Full-time',
    description: 'We are looking for an Azure Data Engineer with 2-5 years of experience to design, build, and maintain data pipelines on Microsoft Azure. You will work with Azure Data Factory, Azure Databricks, Azure Synapse Analytics, and other Azure data services to create scalable data solutions for our clients.',
    requirements: [
      '2-5 years of experience in data engineering',
      'Strong experience with Azure Data Factory, Azure Databricks',
      'Proficiency in SQL and Python',
      'Experience with Azure Synapse Analytics, Azure Data Lake',
      'Knowledge of ETL/ELT processes and data modeling',
      'Understanding of data warehousing concepts',
      'Experience with Azure DevOps and CI/CD pipelines',
      'Strong analytical and problem-solving skills'
    ],
    responsibilities: [
      'Design and implement data pipelines using Azure Data Factory and Databricks',
      'Develop and maintain data warehouses and data lakes on Azure',
      'Create and optimize SQL queries and stored procedures',
      'Implement ETL/ELT processes for data transformation',
      'Monitor and troubleshoot data pipeline performance',
      'Collaborate with data analysts and business stakeholders',
      'Ensure data quality and implement data governance practices',
      'Document data architecture and processes'
    ],
    icon: HiCloud,
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'sql-developer',
    title: 'SQL Developer',
    department: 'Database Development',
    location: 'Indore',
    type: 'Full-time',
    description: 'Join our team as a SQL Developer with 2-5 years of experience. You will be responsible for designing, developing, and optimizing database solutions. Write complex SQL queries, create stored procedures, optimize database performance, and ensure data integrity across various database systems.',
    requirements: [
      '2-5 years of experience as SQL Developer',
      'Expert-level knowledge of SQL and T-SQL',
      'Experience with SQL Server, MySQL, or PostgreSQL',
      'Strong understanding of database design and normalization',
      'Experience writing stored procedures, functions, and triggers',
      'Knowledge of database performance tuning and optimization',
      'Understanding of data warehousing concepts',
      'Experience with ETL processes and data integration'
    ],
    responsibilities: [
      'Design and develop database schemas and structures',
      'Write complex SQL queries, stored procedures, and functions',
      'Optimize database performance and query execution',
      'Ensure data integrity and implement data validation rules',
      'Create and maintain database documentation',
      'Collaborate with application developers on database requirements',
      'Perform database maintenance and backup operations',
      'Troubleshoot and resolve database issues'
    ],
    icon: HiServer,
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'power-bi-expert',
    title: 'Power BI Expert',
    department: 'Business Intelligence',
    location: 'Indore',
    type: 'Full-time',
    description: 'We are seeking a Power BI Expert with 2-5 years of experience to create interactive dashboards, reports, and data visualizations. You will work closely with business stakeholders to understand requirements, design data models, and deliver actionable insights through Power BI solutions.',
    requirements: [
      '2-5 years of experience with Power BI',
      'Expert knowledge of Power BI Desktop, Power BI Service',
      'Strong SQL skills for data extraction and transformation',
      'Experience with DAX (Data Analysis Expressions)',
      'Knowledge of Power Query and M language',
      'Understanding of data modeling and relationships',
      'Experience creating interactive dashboards and reports',
      'Strong analytical and visualization skills'
    ],
    responsibilities: [
      'Design and develop interactive Power BI dashboards and reports',
      'Create data models and establish relationships between data sources',
      'Write DAX expressions for calculated columns and measures',
      'Transform and clean data using Power Query',
      'Collaborate with stakeholders to understand business requirements',
      'Optimize report performance and data refresh schedules',
      'Train end-users on Power BI usage and best practices',
      'Maintain and update existing Power BI solutions'
    ],
    icon: HiChartBar,
    color: 'from-orange-500 to-amber-500'
  }
];

interface ApplicationFormData {
  name: string;
  email: string;
  phone: string;
  coverLetter: string;
  resume: File | null;
}

const ApplicationFormModal: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  position: string;
  jobId: string;
}> = ({ isOpen, onClose, position, jobId }) => {
  const [formData, setFormData] = useState<ApplicationFormData>({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, resume: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = new FormData();
      payload.append('jobId', jobId);
      payload.append('jobTitle', position);
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      if (formData.coverLetter) {
        payload.append('coverLetter', formData.coverLetter);
      }
      if (formData.resume) {
        payload.append('resume', formData.resume);
      }

      await submitJobApplication(payload);
      setSubmitSuccess(true);

      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          coverLetter: '',
          resume: null
        });
        setSubmitSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Failed to submit application. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        coverLetter: '',
        resume: null
      });
      setSubmitSuccess(false);
      setSubmitError('');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-slate-950 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto"
            >
              <div className="sticky top-0 bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between rounded-t-3xl">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Apply for Position</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{position}</p>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors disabled:opacity-50"
                >
                  <IconWrapper icon={HiXMark} className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {submitSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="inline-flex p-4 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
                      <IconWrapper icon={HiCheckCircle} className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Application Submitted!</h3>
                    <p className="text-slate-600 dark:text-slate-400">We'll review your application and get back to you soon.</p>
                  </motion.div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IconWrapper icon={HiUser} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IconWrapper icon={HiEnvelope} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="john.doe@example.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <IconWrapper icon={HiPhone} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Resume/CV <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                          className="hidden"
                          id="resume-upload"
                        />
                        <label
                          htmlFor="resume-upload"
                          className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        >
                          <IconWrapper icon={HiDocumentArrowUp} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          <div className="flex-1">
                            {formData.resume ? (
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">{formData.resume.name}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">{(formData.resume.size / 1024).toFixed(2)} KB</p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">Click to upload resume</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">PDF, DOC, or DOCX (Max 5MB)</p>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                        Cover Letter
                      </label>
                      <textarea
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleInputChange}
                        rows={5}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Tell us why you're interested in this position..."
                      />
                    </div>

                    {submitError && (
                      <p className="text-sm text-red-500">{submitError}</p>
                    )}

                    <div className="flex gap-4 pt-4">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="flex-1 px-6 py-3 border border-slate-200 dark:border-slate-700 rounded-full font-semibold text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          'Submit Application'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const job = jobPositions.find(j => j.id === jobId);

  if (!job) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Job Not Found</h1>
          <Link to="/careers" className="text-blue-600 dark:text-blue-400 hover:underline">
            Back to Careers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Header */}
      <section className="pt-32 pb-12 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/careers"
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6"
          >
            <IconWrapper icon={HiArrowLeft} className="w-5 h-5 mr-2" />
            Back to Careers
          </Link>

          <div className="flex items-start gap-4 mb-6">
            <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${job.color} text-white`}>
              <IconWrapper icon={job.icon} className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-sm font-semibold">
                  {job.department}
                </span>
                <span className="px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-semibold">
                  {job.type}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
                {job.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <IconWrapper icon={HiMapPin} className="w-5 h-5" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <IconWrapper icon={HiClock} className="w-5 h-5" />
                  <span>{job.type}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Details */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[2fr_1fr] gap-8">
            <div className="space-y-8">
              {/* Job Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Job Description</h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  {job.description}
                </p>
              </motion.div>

              {/* Responsibilities */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Key Responsibilities</h2>
                <ul className="space-y-3">
                  {job.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                      <IconWrapper icon={HiCheckCircle} className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Requirements */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Requirements</h2>
                <ul className="space-y-3">
                  {job.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                      <IconWrapper icon={HiCheckCircle} className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{requirement}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:sticky lg:top-24 h-fit">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-lg"
              >
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Job Overview</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Department</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{job.department}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Location</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{job.location}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Job Type</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{job.type}</p>
                  </div>
                </div>
                <motion.button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full mt-6 px-6 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Apply Now
                  <IconWrapper icon={HiCheckCircle} className="w-5 h-5" />
                </motion.button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <ApplicationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        position={job.title}
        jobId={job.id}
      />
    </div>
  );
};

export default JobDetailPage;

